import React, { useState, useCallback, useEffect } from 'react';
import { Dialog, DialogContent, DialogActions, Box, Typography, Button, IconButton } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { useEndpointFetch } from '../network/useEndpointFetch';
import { FileUploadArea, DescriptionInput, ProcessingView, ErrorView, CompleteView } from './components';
import { AlertCard, AlertCardBody, getAlertCardColors } from '../../common/cards/alert-card/AlertCard';
import {
  UploadedFile,
  SetupAnalyseRequest,
  ActivitiesAnalyseResponse,
  ActivitySuggestion,
  ActivitySchedule,
  ClarificationAnswer,
} from './stages/types';
import { ActivitiesResultsView, getInitialActivitySelections } from './stages/activities';
import { useSetupDialogStyles } from './setupDialogStyles';
import { useProgramsContext } from '../programs-sell/useProgramsContext';

type Step = 'upload' | 'processing' | 'results' | 'applying' | 'complete' | 'error';

interface ActivitiesSetupDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete?: (data: Record<string, unknown>) => void;
  questionnaireAnswers?: Record<string, unknown>;
}

interface RecurrenceRequest {
  __typename: string;
  startDate: string;
  endDate?: string | null;
  dayInterval?: number;
  weekInterval?: number;
  days?: string[];
  monthInterval?: number;
  monthDay?: number;
  n?: number;
  day?: string;
  yearInterval?: number;
}

interface SaveActivityRequest {
  activityId?: string;
  programId: string;
  name: string;
  description: string;
  startDate?: string;
  endDate?: string;
  enrollable?: boolean;
  recurrences?: RecurrenceRequest[];
  sessions: SessionRequest[];
  isRestrictedActivity: boolean;
  isHidden: boolean;
  hasOpenRoster: boolean;
  maxTeam?: number;
  allowCICO?: boolean;
  isEvent?: boolean;
  eventDates?: string[];
}

interface SessionRequest {
  sessionId?: string;
  name: string;
  description: string;
  minVolunteers?: number;
  maxVolunteers?: number;
  startTime?: string;
  endTime?: string;
}

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const { result } = reader;
      if (typeof result !== 'string') {
        reject(new Error('Failed to read file as base64'));
        return;
      }
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

// Format time to HH:MM:SS format required by server
function formatTimeWithSeconds(time?: string): string | undefined {
  if (!time) return undefined;
  // If already has seconds, return as is
  if (time.match(/^\d{2}:\d{2}:\d{2}$/)) return time;
  // If HH:MM format, add :00 seconds
  if (time.match(/^\d{2}:\d{2}$/)) return `${time}:00`;
  return undefined;
}

// Get today's date in YYYY-MM-DD format
function getTodayDate(): string {
  const today = new Date();
  return today.toISOString().split('T')[0];
}

// Build recurrence object with proper __typename and fields
function buildRecurrence(schedule: ActivitySchedule, startDate?: string): RecurrenceRequest | null {
  const recurrenceStartDate = startDate || getTodayDate();

  switch (schedule.type) {
    case 'single':
      return {
        __typename: 'SingleRecurrenceType',
        startDate: recurrenceStartDate,
      };
    case 'daily':
      return {
        __typename: 'DailyRecurrenceType',
        startDate: recurrenceStartDate,
        dayInterval: schedule.dayInterval || 1,
      };
    case 'weekly':
      return {
        __typename: 'WeeklyRecurrenceType',
        startDate: recurrenceStartDate,
        days: schedule.days || [],
        weekInterval: schedule.weekInterval || 1,
      };
    case 'monthlyByDate':
      return {
        __typename: 'MonthlyByMonthDayRecurrenceType',
        startDate: recurrenceStartDate,
        monthDay: schedule.monthDay || 1,
        monthInterval: schedule.monthInterval || 1,
      };
    case 'monthlyByNthDay':
      return {
        __typename: 'MonthlyNthDayRecurrenceType',
        startDate: recurrenceStartDate,
        n: schedule.nthWeek || 1,
        day: schedule.dayOfWeek || 'MO',
        monthInterval: schedule.monthInterval || 1,
      };
    case 'yearly':
      return {
        __typename: 'YearlyRecurrenceType',
        startDate: recurrenceStartDate,
        yearInterval: schedule.yearInterval || 1,
      };
    default:
      return null;
  }
}

function toDraftJs(text: string): string {
  if (!text) return '';
  return JSON.stringify({
    blocks: [
      {
        text,
        type: 'unstyled',
        depth: 0,
        inlineStyleRanges: [],
        entityRanges: [],
        data: {},
      },
    ],
    entityMap: {},
  });
}

function buildActivityRequest(
  activity: ActivitySuggestion,
  programId: string,
  editedActivity?: { name: string; description: string }
): SaveActivityRequest {
  const sessions: SessionRequest[] =
    activity.sessions && activity.sessions.length > 0
      ? activity.sessions.map((s) => ({
          name: s.name,
          description: toDraftJs(s.description || ''),
          minVolunteers: s.minVolunteers ?? undefined,
          maxVolunteers: s.maxVolunteers ?? undefined,
          startTime: formatTimeWithSeconds(s.startTime),
          endTime: formatTimeWithSeconds(s.endTime),
        }))
      : [
          {
            name: 'General',
            description: toDraftJs(''),
          },
        ];

  const isEvent = activity.activityType === 'event';

  const recurrences: RecurrenceRequest[] = [];
  if (!isEvent && activity.schedule) {
    const recurrence = buildRecurrence(activity.schedule, activity.startDate);
    if (recurrence) {
      recurrences.push(recurrence);
    }
  }

  return {
    programId,
    name: editedActivity?.name ?? activity.name,
    description: toDraftJs(editedActivity?.description ?? activity.description ?? ''),
    startDate: activity.startDate,
    endDate: activity.endDate,
    enrollable: true,
    recurrences: recurrences.length > 0 ? recurrences : undefined,
    sessions,
    isRestrictedActivity: false,
    isHidden: false,
    hasOpenRoster: true,
    maxTeam: activity.maxTeam,
    allowCICO: true,
    isEvent,
    eventDates: isEvent ? activity.eventDates : undefined,
  };
}

export function ActivitiesSetupDialog({ open, onClose, onComplete, questionnaireAnswers }: ActivitiesSetupDialogProps) {
  const classes = useSetupDialogStyles();
  const { lightTextColor } = getAlertCardColors('info');
  const { programs, queryPrograms } = useProgramsContext();

  const [step, setStep] = useState<Step>('upload');
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [description, setDescription] = useState('');
  const [results, setResults] = useState<ActivitiesAnalyseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedActivities, setSelectedActivities] = useState<Set<string>>(new Set());
  const [editedActivities, setEditedActivities] = useState<Map<string, { name: string; description: string }>>(
    new Map()
  );
  const [activitiesCreated, setActivitiesCreated] = useState(0);
  const [activityProgramMap, setActivityProgramMap] = useState<Map<string, string>>(new Map());
  const [questionsResolved, setQuestionsResolved] = useState(true);

  const analyseFetch = useEndpointFetch<SetupAnalyseRequest, ActivitiesAnalyseResponse>('/vm/setup/analyse');
  const saveActivityFetch = useEndpointFetch<SaveActivityRequest, { activityId: string }>('/vm/activity/save');

  // Load programs when dialog opens
  useEffect(() => {
    if (open) {
      queryPrograms();
    }
  }, [open, queryPrograms]);

  // Auto-assign single program to all activities when only one program exists
  useEffect(() => {
    if (programs && programs.length === 1 && results?.activities) {
      const singleProgramId = programs[0].programId;
      setActivityProgramMap((prev) => {
        const next = new Map(prev);
        results.activities.forEach((activity) => {
          if (!next.has(activity.name)) {
            next.set(activity.name, singleProgramId);
          }
        });
        return next;
      });
    }
  }, [programs, results]);

  const handleFilesAdded = useCallback((newFiles: UploadedFile[]) => {
    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const handleFileRemoved = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_unused, i) => i !== index));
  }, []);

  const handleAnalyse = async (clarificationAnswers?: ClarificationAnswer[]) => {
    setStep('processing');
    setError(null);

    try {
      const fileData = await Promise.all(
        files.map(async (f) => ({
          name: f.name,
          content: await fileToBase64(f.file),
        }))
      );

      const response = await analyseFetch.run({
        section: 'activities',
        files: fileData,
        description: description || undefined,
        questionnaireAnswers,
        clarificationAnswers,
      });

      if (response.ok && response.data?.data) {
        const { data } = response.data;
        setResults(data);
        const selections = getInitialActivitySelections(data);
        setSelectedActivities(selections.activities);
        setStep('results');
      } else {
        const errorMessage = response.data?.message || 'Failed to analyse documents';
        setError(errorMessage);
        setStep('error');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An unexpected error occurred');
      setStep('error');
    }
  };

  const handleReanalyse = (clarificationAnswers: ClarificationAnswer[]) => {
    handleAnalyse(clarificationAnswers);
  };

  const handleQuestionsResolved = useCallback((resolved: boolean) => {
    setQuestionsResolved(resolved);
  }, []);

  const handleReset = () => {
    setStep('upload');
    setFiles([]);
    setDescription('');
    setResults(null);
    setError(null);
    setSelectedActivities(new Set());
    setEditedActivities(new Map());
    setActivitiesCreated(0);
    setActivityProgramMap(new Map());
    setQuestionsResolved(true);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleApply = async () => {
    if (!results) return;

    setStep('applying');

    try {
      const activitiesToCreate = results.activities
        .filter((activity) => selectedActivities.has(activity.name) && activityProgramMap.has(activity.name))
        .map((activity) => {
          const edited = editedActivities.get(activity.name);
          const programId = activityProgramMap.get(activity.name)!;
          return buildActivityRequest(activity, programId, edited);
        });

      const responses = await activitiesToCreate.reduce<Promise<Array<{ ok: boolean; data?: any; error?: any }>>>(
        async (acc, activityData) => {
          const prev = await acc;
          try {
            const response = await saveActivityFetch.run(activityData);
            return [...prev, response || { ok: false }];
          } catch (e) {
            return [...prev, { ok: false, data: null, error: e }];
          }
        },
        Promise.resolve([])
      );

      const created = responses.filter((r) => r.ok).length;
      const failed = responses.length - created;

      if (failed > 0) {
        const failedResponses = responses.filter((r) => !r.ok);
        failedResponses.forEach((r, i) => {
          console.error(`Activity ${i + 1} failed:`, r.data || r);
        });
      }

      if (created === 0 && failed > 0) {
        const firstError = responses.find((r) => !r.ok);
        const message = firstError?.data?.message || 'Failed to create activities';
        setError(`${message}. Please check the activity details and try again.`);
        setStep('error');
      } else {
        setActivitiesCreated(created);
        if (failed > 0) {
          console.warn(`${created} activities created, ${failed} failed`);
        }
        if (onComplete && results) {
          const activitiesCreatedList = results.activities
            .filter((a) => selectedActivities.has(a.name))
            .map((a) => {
              const edited = editedActivities.get(a.name);
              return {
                name: edited?.name ?? a.name,
                description: edited?.description ?? a.description,
              };
            });
          onComplete({
            analysis: results.analysis,
            activitiesCreated: activitiesCreatedList,
            activitiesCreatedCount: created,
            activityPrograms: Object.fromEntries(activityProgramMap),
          });
        }
        setStep('complete');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'An unexpected error occurred');
      setStep('error');
    }
  };

  const handleComplete = () => {
    handleClose();
  };

  const toggleActivity = (activityName: string) => {
    setSelectedActivities((prev) => {
      const next = new Set(prev);
      if (next.has(activityName)) next.delete(activityName);
      else next.add(activityName);
      return next;
    });
  };

  const setActivityProgram = (activityName: string, programId: string) => {
    setActivityProgramMap((prev) => {
      const next = new Map(prev);
      next.set(activityName, programId);
      return next;
    });
  };

  const getSubtitle = () => {
    switch (step) {
      case 'upload':
        return 'Describe your volunteering activities';
      case 'processing':
        return 'Analysing your documents...';
      case 'results':
        return 'Review and select activities to create';
      case 'applying':
        return 'Creating activities...';
      case 'complete':
        return 'Activities created successfully';
      case 'error':
        return 'Something went wrong';
      default:
        return '';
    }
  };

  const canAnalyse = files.length > 0 || description.trim().length > 0;
  // Can apply if activities are selected, all have a program, and questions are resolved
  const allSelectedHaveProgram = Array.from(selectedActivities).every((name) => activityProgramMap.has(name));
  const hasUnresolvedQuestions = !questionsResolved;
  const canApply = selectedActivities.size > 0 && allSelectedHaveProgram && !hasUnresolvedQuestions;

  const noProgramsAvailable = !programs || programs.length === 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth classes={{ paper: classes.dialogPaper }}>
      <Box className={classes.header}>
        <IconButton className={classes.closeButton} onClick={handleClose} size='small'>
          <Close fontSize='small' />
        </IconButton>
        <Typography className={classes.title}>Activities Setup</Typography>
        <Typography className={classes.subtitle}>{getSubtitle()}</Typography>
      </Box>

      <DialogContent className={classes.content}>
        {step === 'upload' && (
          <>
            <Box mb={2}>
              <AlertCard variant='info' title='What is an Activity?'>
                <AlertCardBody>
                  <Typography style={{ fontSize: 14, color: lightTextColor }}>
                    An Activity is a specific volunteering opportunity that volunteers can sign up for. Activities have
                    sessions (time slots), can recur on a schedule, and belong to a Program.
                  </Typography>
                </AlertCardBody>
              </AlertCard>
            </Box>
            <Typography className={classes.description}>
              Upload documents that describe your volunteering activities, rosters, or schedules. This could include CSV
              rosters from other systems, spreadsheets, or copies of rosters distributed to volunteers. Include
              information about when and where activities run, and how many volunteers are needed.
            </Typography>
            <DescriptionInput
              value={description}
              onChange={setDescription}
              label='Describe your activities'
              placeholder='E.g., "We run a weekly food bank on Saturdays 9am-1pm needing 10 volunteers, and a tutoring program on Tuesday and Thursday evenings"'
              helperText='Include details about schedules, locations, and volunteer numbers'
            />
            <Typography className={classes.sectionTitle}>Upload documents</Typography>
            <Box className={classes.uploadSection}>
              <FileUploadArea files={files} onFilesAdded={handleFilesAdded} onFileRemoved={handleFileRemoved} />
            </Box>
          </>
        )}

        {step === 'processing' && (
          <ProcessingView title='Analysing your documents...' subtitle='Identifying activities and schedules' />
        )}

        {step === 'results' && results && (
          <>
            {noProgramsAvailable && (
              <Box mb={2}>
                <AlertCard variant='warning' title='No Programs Available'>
                  <AlertCardBody>
                    <Typography style={{ fontSize: 14 }}>
                      You need to create at least one Program before creating Activities. Please complete the Programs
                      setup step first.
                    </Typography>
                  </AlertCardBody>
                </AlertCard>
              </Box>
            )}

            <ActivitiesResultsView
              results={results}
              selectedActivities={selectedActivities}
              onToggleActivity={toggleActivity}
              programs={programs || []}
              activityProgramMap={activityProgramMap}
              onSetActivityProgram={setActivityProgram}
              onReanalyse={handleReanalyse}
              onQuestionsResolved={handleQuestionsResolved}
            />
          </>
        )}

        {step === 'applying' && <ProcessingView title='Creating activities...' subtitle='Please wait' />}

        {step === 'complete' && (
          <CompleteView
            title='Activities Created'
            subtitle={`${activitiesCreated} activit${activitiesCreated !== 1 ? 'ies' : 'y'} created successfully`}
            linkText='Manage Activities'
            linkHref='/management/activity-management'
          />
        )}

        {step === 'error' && error && <ErrorView message={error} onRetry={handleReset} />}
      </DialogContent>

      <DialogActions className={classes.actions}>
        {step === 'upload' && (
          <>
            <Button className={classes.secondaryButton} onClick={handleClose}>
              Cancel
            </Button>
            <Button className={classes.primaryButton} onClick={() => handleAnalyse()} disabled={!canAnalyse}>
              Analyse
            </Button>
          </>
        )}

        {step === 'results' && (
          <>
            <Button className={classes.secondaryButton} onClick={handleReset}>
              Start Over
            </Button>
            <Button className={classes.primaryButton} onClick={handleApply} disabled={!canApply}>
              Create {selectedActivities.size} Activit{selectedActivities.size !== 1 ? 'ies' : 'y'}
            </Button>
          </>
        )}

        {step === 'results' && hasUnresolvedQuestions && (
          <Typography
            variant='caption'
            color='textSecondary'
            style={{ width: '100%', textAlign: 'center', marginTop: -4 }}
          >
            Answer or skip all questions above to enable creating activities
          </Typography>
        )}

        {step === 'complete' && (
          <>
            <Box />
            <Button className={classes.primaryButton} onClick={handleComplete}>
              Done
            </Button>
          </>
        )}

        {step === 'error' && (
          <>
            <Button className={classes.secondaryButton} onClick={handleClose}>
              Close
            </Button>
            <Button className={classes.primaryButton} onClick={handleReset}>
              Try Again
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
